import { useEffect, useMemo, useState } from "react";
import { ArrowDownToLine, CheckCircle2, ClipboardList, Layers } from "lucide-react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { fmtDate, num } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useInfiniteCatalogProducts } from "../hooks/useInfiniteCatalogProducts";
import {
  Badge,
  Button,
  Input,
  InfoTip,
  PillSegmentSwitcher,
  SearchSelect,
  Textarea,
  PageLoader,
  Toggle,
  statusColor,
} from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { flattenInfinitePages, getNextPageParam } from "../lib/infinite-list";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import {
  movementKindDetail,
  movementRowBadgeColor,
  movementRowLabel,
  normalizeMovementKind,
  STOCK_MOVEMENT_KINDS,
  STOCK_MOVEMENT_KIND_HINTS,
  STOCK_MOVEMENT_KIND_LABELS,
} from "@gaole/shared";

type Tab = "movements" | "allocations" | "adjust" | "audits" | "lowstock";

interface MovementRow {
  id: string;
  productId: string;
  kind: string;
  kindLabel?: string;
  delta: number;
  reservedDelta: number | null;
  note: string | null;
  refType: string | null;
  refId: string | null;
  refLabel: string | null;
  createdAt: string;
  product: { sku: string; name: string };
  balance: {
    onHand: number;
    warehouseFree: number;
    shopAvailable: number;
    inRaffles: number;
    inAuctions: number;
    inPacks: number;
  } | null;
}

interface AllocationRow {
  id: string;
  productId: string;
  productSku: string;
  productName: string;
  refType: "PACK" | "RAFFLE" | "AUCTION" | "ORDER";
  refId: string;
  qtyOpen: number;
  label: string;
  status: string | null;
}

interface LowStockRow {
  id: string;
  sku: string;
  name: string;
  type?: string;
  typeLabel?: string;
  onHand: number;
  reserved: number;
  available: number;
  threshold: number;
}

interface AuditSummary {
  id: string;
  status: string;
  note: string | null;
  createdAt: string;
  completedAt: string | null;
}

interface AuditItem {
  productId: string;
  sku: string;
  name: string;
  expected: number;
  counted: number | null;
}

interface AuditDetail {
  audit: {
    id: string;
    status: string;
    note: string | null;
    createdAt: string;
    completedAt: string | null;
    items: AuditItem[];
  };
}

interface ProductOption {
  id: string;
  sku: string;
  name: string;
  images?: string[];
}

const PAGE_SIZE = 50;

function movementBalanceCell(value: number | undefined, accentClass = "text-white/70") {
  if (value === undefined) {
    return <span className="text-xs text-white/25">—</span>;
  }
  return <span className={`font-display text-xs font-semibold tabular-nums ${accentClass}`}>{num(value)}</span>;
}

export function Inventory() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState<Tab>(() => {
    const t = searchParams.get("tab");
    return t === "allocations" || t === "adjust" || t === "audits" || t === "lowstock" ? t : "movements";
  });
  const [movementProductId, setMovementProductId] = useState(() => searchParams.get("productId") ?? "");
  const [movementKind, setMovementKind] = useState("");
  const [mechanicsOnly, setMechanicsOnly] = useState(false);
  const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);
  const [countedValues, setCountedValues] = useState<Record<string, string>>({});

  const [adjustProductId, setAdjustProductId] = useState("");
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustNote, setAdjustNote] = useState("");
  const [auditNote, setAuditNote] = useState("");

  const canStock = can("catalog.stock");

  const movementsQuery = useInfiniteQuery({
    queryKey: ["inventory-movements", movementProductId, movementKind, mechanicsOnly],
    initialPageParam: 1,
    enabled: tab === "movements",
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        pageSize: String(PAGE_SIZE),
      });
      if (movementProductId) params.set("productId", movementProductId);
      if (movementKind) params.set("kind", movementKind);
      if (mechanicsOnly) params.set("mechanicsOnly", "true");
      return api<{ items: MovementRow[]; total: number }>(`/inventory/movements?${params}`);
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(
        lastPage.total,
        allPages.reduce((acc, page) => acc + page.items.length, 0),
        PAGE_SIZE,
      ),
  });

  const allocationsQuery = useInfiniteQuery({
    queryKey: ["inventory-allocations", movementProductId],
    initialPageParam: 1,
    enabled: tab === "allocations",
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        pageSize: String(PAGE_SIZE),
      });
      if (movementProductId) params.set("productId", movementProductId);
      return api<{ items: AllocationRow[]; total: number }>(`/inventory/allocations?${params}`);
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(
        lastPage.total,
        allPages.reduce((acc, page) => acc + page.items.length, 0),
        PAGE_SIZE,
      ),
  });

  const movementRows = flattenInfinitePages(movementsQuery.data?.pages);
  const movementTotal = movementsQuery.data?.pages[0]?.total ?? 0;
  const movementHasNext =
    movementRows.length < movementTotal && (movementsQuery.hasNextPage ?? false);

  const allocationRows = flattenInfinitePages(allocationsQuery.data?.pages);
  const allocationTotal = allocationsQuery.data?.pages[0]?.total ?? 0;
  const allocationHasNext =
    allocationRows.length < allocationTotal && (allocationsQuery.hasNextPage ?? false);

  const [productSearch, setProductSearch] = useState("");
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("");

  useEffect(() => {
    const t = globalThis.setTimeout(() => setDebouncedProductSearch(productSearch.trim()), 250);
    return () => globalThis.clearTimeout(t);
  }, [productSearch]);

  const filterProductsQuery = useInfiniteCatalogProducts({
    enabled: tab === "movements" || tab === "allocations",
    search: debouncedProductSearch,
    queryKey: ["inventory-filter"],
    params: { forInventory: "true", behavior: "SINGLE" },
  });

  const lowStockQuery = useQuery({
    queryKey: ["inventory-low-stock"],
    queryFn: () => api<{ items: LowStockRow[] }>("/inventory/low-stock"),
    enabled: tab === "lowstock",
  });

  const auditsQuery = useQuery({
    queryKey: ["inventory-audits"],
    queryFn: () => api<{ items: AuditSummary[] }>("/inventory/audits"),
    enabled: tab === "audits",
  });

  const auditDetailQuery = useQuery({
    queryKey: ["inventory-audit", selectedAuditId],
    queryFn: () => api<AuditDetail>(`/inventory/audits/${selectedAuditId}`),
    enabled: !!selectedAuditId,
  });

  const productsQuery = useInfiniteCatalogProducts({
    enabled: tab === "adjust",
    search: debouncedProductSearch,
    queryKey: ["inventory-adjust"],
    params: { forInventory: "true", behavior: "SINGLE" },
  });

  const productAdjustOptions = useMemo(
    () => [
      { value: "", label: "Choose product…" },
      ...productsQuery.items.map((p) => ({
        value: p.id,
        label: `${p.sku ?? "—"} — ${p.name}`,
        image: p.images?.[0] ?? null,
      })),
    ],
    [productsQuery.items],
  );

  const adjustMutation = useMutation({
    mutationFn: (body: { productId: string; delta: number; note: string }) =>
      api("/inventory/adjust", { method: "POST", body }),
    onSuccess: () => {
      toast("success", "Stock adjusted");
      setAdjustDelta("");
      setAdjustNote("");
      void queryClient.invalidateQueries({ queryKey: ["inventory-movements"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory-low-stock"] });
      void queryClient.invalidateQueries({ queryKey: ["catalog-products"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Adjust failed"),
  });

  const createAuditMutation = useMutation({
    mutationFn: (note?: string) => api<{ id: string }>("/inventory/audits", { method: "POST", body: { note } }),
    onSuccess: (data) => {
      toast("success", "Stock take started");
      setAuditNote("");
      setSelectedAuditId(data.id);
      void queryClient.invalidateQueries({ queryKey: ["inventory-audits"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Could not start stock take"),
  });

  const completeAuditMutation = useMutation({
    mutationFn: (args: { id: string; items: { productId: string; counted: number }[] }) =>
      api(`/inventory/audits/${args.id}/complete`, { method: "POST", body: { items: args.items } }),
    onSuccess: () => {
      toast("success", "Stock take completed");
      setSelectedAuditId(null);
      setCountedValues({});
      void queryClient.invalidateQueries({ queryKey: ["inventory-audits"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory-movements"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory-low-stock"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Could not complete stock take"),
  });

  const productFilterOptions = useMemo(
    () => [
      { value: "", label: "All products" },
      ...filterProductsQuery.items.map((p) => ({
        value: p.id,
        label: `${p.sku ?? "—"} — ${p.name}`,
        image: p.images?.[0] ?? null,
      })),
    ],
    [filterProductsQuery.items],
  );

  const refTypeBadge = (refType: string | null) => {
    if (refType === "PACK") return "pink";
    if (refType === "RAFFLE") return "blue";
    if (refType === "AUCTION") return "amber";
    if (refType === "ORDER") return "green";
    return "gray";
  };

  const allocationTypeBadge = (refType: AllocationRow["refType"]) => {
    if (refType === "PACK") return "pink";
    if (refType === "RAFFLE") return "blue";
    if (refType === "ORDER") return "green";
    return "amber";
  };

  const inventoryFilterBar = (tabLabel: string) => (
    <div className="flex flex-col gap-3 rounded-2xl border border-line bg-surface p-3 sm:flex-row sm:items-end">
      <SearchSelect
        className="min-w-0 flex-1"
        label="Product"
        placeholder="All products"
        searchPlaceholder="Search products…"
        options={productFilterOptions}
        value={movementProductId}
        onChange={(v) => {
          setMovementProductId(v);
          const next = new URLSearchParams(searchParams);
          if (v) next.set("productId", v);
          else next.delete("productId");
          setSearchParams(next, { replace: true });
        }}
        onSearchChange={setProductSearch}
        serverFiltered
        emptyMessage={filterProductsQuery.isLoading ? "Loading products…" : "No matches"}
        hasMore={filterProductsQuery.hasNextPage}
        loadingMore={filterProductsQuery.isFetchingNextPage || filterProductsQuery.isLoading}
        onLoadMore={filterProductsQuery.fetchNextPage}
      />
      {tabLabel === "movements" && (
        <>
          <SearchSelect
            className="sm:w-52"
            label="Kind"
            placeholder="All kinds"
            options={[
              { value: "", label: "All kinds" },
              ...STOCK_MOVEMENT_KINDS.map((k) => ({ value: k, label: STOCK_MOVEMENT_KIND_LABELS[k] })),
            ]}
            value={movementKind}
            onChange={setMovementKind}
          />
          <Toggle
            checked={mechanicsOnly}
            onChange={(v) => {
              setMechanicsOnly(v);
            }}
            label="Mechanics only"
          />
          <InfoTip label="Movement kind definitions" className="self-end">
            <p className="mb-2 font-display text-xs font-semibold text-foreground">Movement kinds</p>
            <ul className="space-y-2 text-xs leading-relaxed">
              {STOCK_MOVEMENT_KINDS.map((k) => (
                <li key={k}>
                  <span className="font-medium text-foreground">{STOCK_MOVEMENT_KIND_LABELS[k]}</span>
                  <span className="text-muted"> — {STOCK_MOVEMENT_KIND_HINTS[k]}</span>
                </li>
              ))}
            </ul>
          </InfoTip>
        </>
      )}
    </div>
  );

  const movementColumns: Column<MovementRow>[] = useMemo(
    () => [
      {
        key: "when",
        header: "When",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.createdAt,
        exportValue: (r) => fmtDate(r.createdAt),
        render: (r) => <span className="text-xs text-white/50">{fmtDate(r.createdAt)}</span>,
      },
      {
        key: "product",
        header: "Product",
        sortable: true,
        sortValue: (r) => r.product.name,
        exportValue: (r) => `${r.product.name} (${r.product.sku})`,
        render: (r) => (
          <div>
            <p className="text-sm">{r.product.name}</p>
            <p className="font-mono text-[10px] text-white/40">{r.product.sku}</p>
          </div>
        ),
      },
      {
        key: "kind",
        header: "Kind",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.kind,
        render: (r) => (
          <div>
            <Badge color={movementRowBadgeColor({ kind: r.kind, delta: r.delta, refType: r.refType })}>
              {r.kindLabel ??
                movementRowLabel({ kind: r.kind, delta: r.delta, refType: r.refType })}
            </Badge>
            {normalizeMovementKind(r.kind) !== r.kind && movementKindDetail(r.kind, r.note) && (
              <p className="mt-0.5 text-[10px] text-white/45">{movementKindDetail(r.kind, r.note)}</p>
            )}
          </div>
        ),
      },
      {
        key: "reference",
        header: "Reference",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.refLabel ?? r.refType ?? "",
        exportValue: (r) => r.refLabel ?? r.refType ?? "",
        render: (r) =>
          r.refType ? (
            <div>
              <Badge color={refTypeBadge(r.refType)}>{r.refType}</Badge>
              {r.refLabel && <p className="mt-0.5 truncate text-[10px] text-white/50">{r.refLabel}</p>}
            </div>
          ) : (
            <span className="text-xs text-white/30">—</span>
          ),
      },
      {
        key: "delta",
        header: "Change",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.reservedDelta ?? r.delta,
        exportValue: (r) => r.reservedDelta ?? r.delta,
        render: (r) => {
          if (r.kind === "RESERVE" && r.reservedDelta) {
            return (
              <span className="font-display font-semibold text-amber-300" title="Held from warehouse free stock">
                −{num(r.reservedDelta)} WH free
              </span>
            );
          }
          if (r.kind === "ADJUSTMENT" && r.reservedDelta) {
            return (
              <span className="font-display font-semibold text-emerald-300" title="Returned to warehouse free stock">
                +{num(r.reservedDelta)} WH free
              </span>
            );
          }
          return (
            <span className={`font-display font-semibold ${r.delta >= 0 ? "text-emerald-300" : "text-red-300"}`}>
              {r.delta >= 0 ? "+" : ""}
              {num(r.delta)} wh
            </span>
          );
        },
      },
      {
        key: "total",
        header: "Total",
        className: "text-right",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.balance?.onHand ?? -1,
        exportValue: (r) => r.balance?.onHand ?? "",
        render: (r) => (
          <span className="font-display text-xs font-semibold tabular-nums text-white/70" title="Warehouse on hand">
            {r.balance?.onHand !== undefined ? num(r.balance.onHand) : "—"}
          </span>
        ),
      },
      {
        key: "whFree",
        header: "WH free",
        className: "text-right",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.balance?.warehouseFree ?? -1,
        exportValue: (r) => r.balance?.warehouseFree ?? "",
        render: (r) =>
          movementBalanceCell(
            r.balance?.warehouseFree,
            (r.balance?.warehouseFree ?? 0) > 0 ? "text-foreground" : "text-amber-300",
          ),
      },
      {
        key: "shop",
        header: "Shop",
        className: "text-right",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.balance?.shopAvailable ?? -1,
        exportValue: (r) => r.balance?.shopAvailable ?? "",
        render: (r) =>
          movementBalanceCell(
            r.balance?.shopAvailable,
            (r.balance?.shopAvailable ?? 0) > 0 ? "text-emerald-300" : "text-white/40",
          ),
      },
      {
        key: "raffle",
        header: "Raffle",
        className: "text-right",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.balance?.inRaffles ?? -1,
        exportValue: (r) => r.balance?.inRaffles ?? "",
        render: (r) =>
          movementBalanceCell(
            r.balance?.inRaffles,
            (r.balance?.inRaffles ?? 0) > 0 ? "text-blue-300" : "text-white/40",
          ),
      },
      {
        key: "auction",
        header: "Auction",
        className: "text-right",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.balance?.inAuctions ?? -1,
        exportValue: (r) => r.balance?.inAuctions ?? "",
        render: (r) =>
          movementBalanceCell(
            r.balance?.inAuctions,
            (r.balance?.inAuctions ?? 0) > 0 ? "text-amber-300" : "text-white/40",
          ),
      },
      {
        key: "pack",
        header: "Pack",
        className: "text-right",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.balance?.inPacks ?? -1,
        exportValue: (r) => r.balance?.inPacks ?? "",
        render: (r) =>
          movementBalanceCell(
            r.balance?.inPacks,
            (r.balance?.inPacks ?? 0) > 0 ? "text-brand-pink" : "text-white/40",
          ),
      },
      {
        key: "note",
        header: "Note",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.note ?? "",
        render: (r) => <span className="text-xs text-white/50">{r.note ?? "—"}</span>,
      },
    ],
    [],
  );

  const allocationColumns: Column<AllocationRow>[] = useMemo(
    () => [
      {
        key: "product",
        header: "Product",
        sortable: true,
        sortValue: (r) => r.productName,
        exportValue: (r) => `${r.productName} (${r.productSku})`,
        render: (r) => (
          <div>
            <p className="text-sm">{r.productName}</p>
            <p className="font-mono text-[10px] text-white/40">{r.productSku}</p>
          </div>
        ),
      },
      {
        key: "location",
        header: "Location",
        sortable: true,
        sortValue: (r) => r.refType,
        render: (r) => <Badge color={allocationTypeBadge(r.refType)}>{r.refType}</Badge>,
      },
      {
        key: "label",
        header: "Source",
        sortable: true,
        sortValue: (r) => r.label,
        render: (r) => (
          <div>
            <p className="truncate text-sm">{r.label}</p>
            {r.status && <p className="text-[10px] text-white/40">{r.status}</p>}
          </div>
        ),
      },
      {
        key: "qty",
        header: "Qty",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.qtyOpen,
        render: (r) => <span className="font-display font-semibold tabular-nums">{num(r.qtyOpen)}</span>,
      },
    ],
    [],
  );

  const lowStockColumns: Column<LowStockRow>[] = useMemo(
    () => [
      {
        key: "product",
        header: "Product",
        sortable: true,
        sortValue: (r) => r.name,
        exportValue: (r) => `${r.name} (${r.sku})`,
        render: (r) => (
          <div>
            <p className="text-sm">{r.name}</p>
            <p className="font-mono text-[10px] text-white/40">
              {r.sku}
              {r.typeLabel && r.typeLabel !== "Single" ? ` · ${r.typeLabel}` : ""}
            </p>
          </div>
        ),
      },
      {
        key: "onHand",
        header: "On hand",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.onHand,
        render: (r) => <span className="font-display font-semibold text-amber-300 tabular-nums">{num(r.onHand)}</span>,
      },
      {
        key: "reserved",
        header: "Reserved",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.reserved,
        render: (r) => <span className="font-display font-semibold tabular-nums">{num(r.reserved)}</span>,
      },
      {
        key: "balance",
        header: "WH free",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.available,
        exportValue: (r) => r.available,
        render: (r) => (
          <span
            className={`font-display font-semibold tabular-nums ${r.available <= 0 ? "text-red-300" : r.available <= r.threshold ? "text-amber-300" : "text-emerald-300"}`}
          >
            {num(r.available)}
          </span>
        ),
      },
    ],
    [],
  );

  const auditColumns: Column<AuditSummary>[] = useMemo(
    () => [
      {
        key: "created",
        header: "Started",
        sortable: true,
        sortValue: (r) => r.createdAt,
        exportValue: (r) => fmtDate(r.createdAt),
        render: (r) => <span className="text-xs text-white/50">{fmtDate(r.createdAt)}</span>,
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        sortValue: (r) => r.status,
        render: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
      },
      {
        key: "note",
        header: "Note",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.note ?? "",
        render: (r) => <span className="text-xs text-white/60">{r.note ?? "—"}</span>,
      },
      {
        key: "completed",
        header: "Completed",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.completedAt ?? "",
        exportValue: (r) => (r.completedAt ? fmtDate(r.completedAt) : ""),
        render: (r) => (
          <span className="text-xs text-white/50">{r.completedAt ? fmtDate(r.completedAt) : "—"}</span>
        ),
      },
    ],
    [],
  );

  const audit = auditDetailQuery.data?.audit;

  return (
    <ListPageShell>
      <div className="shrink-0 overflow-x-auto pb-1">
        <PillSegmentSwitcher
          options={[
            { value: "movements" as Tab, label: "Movements" },
            { value: "allocations" as Tab, label: "Allocations", badge: allocationTotal || undefined },
            { value: "adjust" as Tab, label: "Adjust" },
            { value: "audits" as Tab, label: "Stock take" },
            { value: "lowstock" as Tab, label: "Low stock", badge: lowStockQuery.data?.items.length },
          ]}
          value={tab}
          onChange={(v) => {
            setTab(v);
            if (v !== "audits") setSelectedAuditId(null);
            const next = new URLSearchParams(searchParams);
            next.set("tab", v);
            setSearchParams(next, { replace: true });
          }}
        />
      </div>

      {tab === "movements" && (
          <>
            {inventoryFilterBar("movements")}
            <p className="text-xs text-white/45">
              Balance columns show totals immediately after each row. <strong>Total</strong> = warehouse on-hand.
              <strong> WH free</strong> = on-hand minus all holds (raffle, pack, auction, checkout). Reserve/release
              rows change WH free and mechanic columns, not Total. Filter by product for the clearest trail.
            </p>
            <DataTable
              columns={movementColumns}
              rows={movementRows}
              rowKey={(r) => r.id}
              loading={movementsQuery.isLoading}
              exportFilename="inventory-movements"
              infiniteScroll={{
                hasNextPage: movementHasNext,
                isFetchingNextPage: movementsQuery.isFetchingNextPage,
                onLoadMore: () => void movementsQuery.fetchNextPage(),
              }}
              empty={{ icon: ArrowDownToLine, title: "No movements yet" }}
            />
          </>
        )}

      {tab === "allocations" && (
        <>
          {inventoryFilterBar("allocations")}
          <p className="text-xs text-white/45">
            Units reserved for packs, raffles, auctions, and unpaid shop orders (Confirm). WH free = on-hand minus
            all reservations. Shop availability is separate — only units on an active shop listing.
          </p>
          <DataTable
            columns={allocationColumns}
            rows={allocationRows}
            rowKey={(r) => r.id}
            loading={allocationsQuery.isLoading}
            exportFilename="inventory-allocations"
            infiniteScroll={{
              hasNextPage: allocationHasNext,
              isFetchingNextPage: allocationsQuery.isFetchingNextPage,
              onLoadMore: () => void allocationsQuery.fetchNextPage(),
            }}
            empty={{ icon: Layers, title: "No mechanic allocations" }}
          />
        </>
      )}

      {tab === "adjust" && (
        <div className="page-scroll max-w-md space-y-4 rounded-2xl border border-line bg-surface p-5">
          {!canStock ? (
            <p className="text-sm text-white/50">You need catalog.stock permission to adjust inventory.</p>
          ) : (
            <>
              <SearchSelect
                label="Product"
                placeholder="Choose product…"
                searchPlaceholder="Search products…"
                options={productAdjustOptions}
                value={adjustProductId}
                onChange={setAdjustProductId}
                onSearchChange={setProductSearch}
                serverFiltered
                emptyMessage={productsQuery.isLoading ? "Loading products…" : "No matches"}
                hasMore={productsQuery.hasNextPage}
                loadingMore={productsQuery.isFetchingNextPage || productsQuery.isLoading}
                onLoadMore={productsQuery.fetchNextPage}
              />
              <Input
                label="Quantity change"
                type="number"
                hint="Positive to add, negative to remove"
                value={adjustDelta}
                onChange={(e) => setAdjustDelta(e.target.value)}
              />
              <Textarea
                label="Reason"
                rows={3}
                hint="Use for lost items, count discrepancies, or other manual corrections only."
                placeholder="Why is stock being adjusted?"
                value={adjustNote}
                onChange={(e) => setAdjustNote(e.target.value)}
              />
              <Button
                disabled={
                  adjustMutation.isPending ||
                  !adjustProductId ||
                  !adjustDelta ||
                  adjustNote.trim().length < 3
                }
                onClick={() =>
                  adjustMutation.mutate({
                    productId: adjustProductId,
                    delta: Number.parseInt(adjustDelta, 10),
                    note: adjustNote.trim(),
                  })
                }
              >
                Adjust stock
              </Button>
            </>
          )}
        </div>
      )}

      {tab === "audits" && (
        <>
          {canStock && (
            <div className="flex flex-col gap-3 rounded-2xl border border-line bg-surface p-4 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <Input
                  label="Note"
                  placeholder="Optional note for this stock take"
                  value={auditNote}
                  onChange={(e) => setAuditNote(e.target.value)}
                />
              </div>
              <Button
                size="sm"
                disabled={createAuditMutation.isPending}
                onClick={() => createAuditMutation.mutate(auditNote.trim() || undefined)}
              >
                Start stock take
              </Button>
            </div>
          )}
          <DataTable
              columns={auditColumns}
              rows={auditsQuery.data?.items}
              rowKey={(r) => r.id}
              loading={auditsQuery.isLoading}
              exportFilename="inventory-stock-takes"
              onRowClick={(r) => {
                if (r.status !== "COMPLETED") setSelectedAuditId(r.id);
              }}
              activeKey={selectedAuditId}
              empty={{ icon: ClipboardList, title: "No stock takes yet" }}
            />
        </>
      )}

      {tab === "lowstock" && (
          <DataTable
            columns={lowStockColumns}
            rows={lowStockQuery.data?.items}
            rowKey={(r) => r.id}
            loading={lowStockQuery.isLoading}
            exportFilename="inventory-low-stock"
            empty={{ icon: CheckCircle2, title: "All stock levels healthy" }}
          />
        )}

      <RightSidebarPanel
        open={!!selectedAuditId && !!audit}
        title="Stock take"
        subtitle={audit?.status}
        wide
        onClose={() => {
          setSelectedAuditId(null);
          setCountedValues({});
        }}
        footer={
          audit?.status !== "COMPLETED" &&
          canStock && (
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                disabled={completeAuditMutation.isPending}
                onClick={() => {
                  const items = (audit?.items ?? [])
                    .map((item) => {
                      const raw = countedValues[item.productId];
                      if (raw === undefined || raw === "") return null;
                      return { productId: item.productId, counted: Number.parseInt(raw, 10) };
                    })
                    .filter((i): i is { productId: string; counted: number } => i !== null);
                  if (items.length === 0) {
                    toast("error", "Enter at least one counted quantity");
                    return;
                  }
                  completeAuditMutation.mutate({ id: audit!.id, items });
                }}
              >
                Complete stock take
              </Button>
            </div>
          )
        }
      >
        {auditDetailQuery.isLoading ? (
          <PageLoader />
        ) : audit ? (
          <div className="space-y-3 text-sm">
            {audit.note && <p className="text-xs text-white/50">{audit.note}</p>}
            <ul className="space-y-2">
              {audit.items.map((item) => (
                <li
                  key={item.productId}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-white/[0.02] px-3 py-2 text-xs"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{item.name}</p>
                    <p className="font-mono text-[10px] text-white/40">{item.sku}</p>
                  </div>
                  <span className="font-display font-semibold tabular-nums text-white/50">
                    Expected {num(item.expected)}
                  </span>
                  {audit.status === "COMPLETED" ? (
                    <span className="font-display font-semibold tabular-nums">Counted {num(item.counted ?? 0)}</span>
                  ) : (
                    <Input
                      type="number"
                      min="0"
                      placeholder="Counted"
                      className="!w-24"
                      value={countedValues[item.productId] ?? ""}
                      onChange={(e) =>
                        setCountedValues((prev) => ({ ...prev, [item.productId]: e.target.value }))
                      }
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </RightSidebarPanel>
    </ListPageShell>
  );
}
